import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Star, Banknote, BookOpen, Filter, GraduationCap, ChevronRight } from "lucide-react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import BookingDialog from "../components/BookingDialog";
import { API, useAuth } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

export default function EducatorsList() {
  const { t } = useTranslation();
  const fmtXof = useFmtXof();
  const { user } = useAuth();
  const isParent = user && typeof user === "object" && user.role === "parent";
  const [educators, setEducators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ subject: "", level: "", city: "" });
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingEducator, setBookingEducator] = useState(null);

  const load = async (q = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.subject) params.set("subject", q.subject);
      if (q.level) params.set("level", q.level);
      if (q.city) params.set("city", q.city);
      const { data } = await axios.get(`${API}/educators?${params.toString()}`);
      setEducators(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSubmit = (e) => { e.preventDefault(); load(filters); };

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="educators-list-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <span className="pill"><GraduationCap className="h-3.5 w-3.5" /> {t("educatorsList.pill")}</span>
          <h1 className="mt-4 font-display text-5xl font-bold text-[#0A2518]">{t("educatorsList.title")}</h1>
          <p className="mt-3 text-[#4B5563] max-w-2xl mx-auto">{t("educatorsList.intro")}</p>
        </div>

        <form onSubmit={onSubmit} className="rounded-3xl bg-white border border-[#E5EBE7] p-5 mb-10 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <Input placeholder={t("educatorsList.subjectPh")} value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} className="h-11" data-testid="filter-subject" />
          </div>
          <div className="md:col-span-1">
            <Input placeholder={t("educatorsList.levelPh")} value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })} className="h-11" data-testid="filter-level" />
          </div>
          <div className="md:col-span-1">
            <Input placeholder={t("educatorsList.cityPh")} value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="h-11" data-testid="filter-city" />
          </div>
          <Button type="submit" className="h-11 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="filter-submit">
            <Search className="h-4 w-4 mr-2" /> {t("educatorsList.filter")}
          </Button>
        </form>

        {loading ? (
          <p className="text-center text-[#4B5563]">{t("educatorsList.loading")}</p>
        ) : educators.length === 0 ? (
          <div className="text-center py-16">
            <Filter className="h-10 w-10 text-[#16A34A]/40 mx-auto mb-3" />
            <p className="text-[#4B5563]">{t("educatorsList.noResults")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="educators-grid">
            {educators.map((edu) => {
              const p = edu.profile || {};
              return (
                <Card key={edu.id} className="p-6 border-[#E5EBE7] card-hover rounded-2xl">
                  <Link to={`/educators-list/${edu.id}`} className="block" data-testid={`educator-link-${edu.id}`}>
                    <h3 className="font-display text-xl font-semibold text-[#0A2518] hover:text-[#15803D] transition-colors">{edu.name}</h3>
                  </Link>
                  <div className="mt-1 flex items-center gap-3 text-sm text-[#4B5563]">
                    {p.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#16A34A]" />{p.city}</span>}
                    <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />{(p.rating ?? 5).toFixed(1)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(p.subjects || []).slice(0, 3).map((s) => (
                      <Badge key={s} className="bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/30">{s}</Badge>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-[#4B5563] line-clamp-3">{p.bio}</p>
                  <div className="mt-4 flex items-center justify-between text-sm text-[#4B5563]">
                    <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5 text-[#16A34A]" /> {t("educatorsList.yearsShort", { years: p.experience_years || 0 })}</span>
                    <span className="flex items-center gap-1"><Banknote className="h-3.5 w-3.5 text-[#16A34A]" /> {fmtXof(p.hourly_rate)}/h</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <Button asChild variant="outline" className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]" data-testid={`view-profile-${edu.id}`}>
                      <Link to={`/educators-list/${edu.id}`}>{t("educatorsList.viewProfile")}</Link>
                    </Button>
                    {isParent ? (
                      <Button
                        onClick={() => { setBookingEducator(edu); setBookingOpen(true); }}
                        className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white"
                        data-testid={`book-educator-${edu.id}`}
                      >
                        {t("educatorsList.book")} <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    ) : (
                      <Button asChild className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                        <Link to="/login">{t("educatorsList.login")}</Link>
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-[#4B5563] mb-3">{t("educatorsList.wantPersonal")}</p>
          <Button asChild className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
            <Link to="/match">{t("educatorsList.launchAi")}</Link>
          </Button>
        </div>
      </div>

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} educator={bookingEducator} />
    </div>
  );
}
